import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTerminology } from "../context/TerminologyContext";
import AppIcon from "./AppIcon";
import logo from "../assets/brand/old/logo.png";

function NavItem({ to, label, icon, collapsed }) {
  return (
    <NavLink to={to} end title={collapsed ? label : undefined}>
      <span className="nav-icon" aria-hidden="true">
        <AppIcon name={icon} />
      </span>
      <span className="label">{label}</span>
    </NavLink>
  );
}

export default function Sidebar({
  collapsed = false,
  setCollapsed = () => {},
}) {
  const { user } = useAuth();
  const { t, tp } = useTerminology();
  const role = user?.currentRole;

  const itemsFor = (r) => {
    switch (r) {
      case "User":
        return [
          ["/", "Status", "status"],
          ["/user/coa", "COA Extractions", "coa"],
          ["/retrieve", "Retrieve COAs", "retrieve"],
          ["/charts", "Charts", "charts"],
          ["/notifications", "Notifications", "notifications"],
          ["/kpi", "KPI Dashboard", "kpi"],
          ["/usage", "Usage Ledger", "usage"],
          ["/audit", "Audit Trail", "audit"],
        ];
      case "Viewer":
        return [
          ["/", "Status", "status"],
          ["/retrieve", "Retrieve COAs", "retrieve"],
          ["/charts", "Charts", "charts"],
          ["/notifications", "Notifications", "notifications"],
          ["/kpi", "KPI Dashboard", "kpi"],
          ["/usage", "Usage Ledger", "usage"],
          ["/audit", "Audit Trail", "audit"],
        ];
      case "Vendor":
        // Vendor Charts removed: vendors are shielded from charts/quota/queue details.
        return [
          ["/vendor/upload", "Upload COAs", "upload"],
          ["/", "Status", "status"],
          ["/notifications", "Notifications", "notifications"],
        ];
      case "Manager":
        return [
          [
            "/manager/review-materials",
            `${tp("vendor")} & ${tp("material")}`,
            "review",
          ],
          ["/manager/review-coas", "Review COAs", "coa"],
          ["/", "Status", "status"],
          ["/retrieve", "Retrieve COAs", "retrieve"],
          ["/charts", "Charts", "charts"],
          ["/notifications", "Notifications", "notifications"],
          ["/kpi", "KPI Dashboard", "kpi"],
          ["/usage", "Usage Ledger", "usage"],
          ["/audit", "Audit Trail", "audit"],
        ];
      case "Super Admin":
        return [
          [
            "/superadmin/vendors",
            `${t("vendor")} & ${tp("material")}`,
            "vendors",
          ],
          ["/superadmin/users", "Add & Manage Teams", "users"],
          ["/superadmin/failed-lots", "Failed Lots", "coa"],
          ["/", "Status", "status"],
          ["/notifications", "Notifications", "notifications"],
          ["/usage", "Usage Ledger", "usage"],
          ["/audit", "Audit Trail", "audit"],
          ["/superadmin/notify", "Configure Notifications", "settings"],
        ];
      case "Admin":
        return [
          ["/admin/vendors", `${t("vendor")} & ${tp("material")}`, "vendors"],
          ["/admin/users", "Add & Manage Users", "users"],
          ["/", "Status", "status"],
          ["/notifications", "Notifications", "notifications"],
          ["/usage", "Usage Ledger", "usage"],
          ["/audit", "Audit Trail", "audit"],
          ["/admin/notify", "Configure Notifications", "settings"],
        ];
      default:
        return [["/", "Status", "status"]];
    }
  };

  const items = itemsFor(role);
  const collapseLabel = collapsed ? "Expand menu" : "Collapse menu";

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-top">
        <div className="logo">
          <div className="sidebar-logo-mark">
            <img src={logo} alt="QualiGate" />
          </div>
          <div className="sidebar-brand-copy">
            <span className="brand" aria-label="QualiGate">
              <span className="brand-quali">Quali</span><span className="brand-gate">Gate</span>
            </span>
            <small>COA operations workspace</small>
          </div>
        </div>
        <button
          className="collapse-btn"
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          title={collapseLabel}
          aria-label={collapseLabel}
        >
          <span className="chev" aria-hidden="true">
            <AppIcon name={collapsed ? "chevron-right" : "chevron-left"} />
          </span>
          <span className="sr-only">{collapseLabel}</span>
        </button>
      </div>

      <nav>
        {items.map(([to, label, icon]) => (
          <NavItem
            key={to}
            to={to}
            label={label}
            icon={icon}
            collapsed={collapsed}
          />
        ))}

        {role === "Vendor" && (
          <a
            href="/documents/User-Manual.pdf"
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? "User Manual" : undefined}
          >
            <span className="nav-icon" aria-hidden="true">
              <AppIcon name="retrieve" />
            </span>

            <span className="label">
              User Manual
            </span>
          </a>
        )}
      </nav>
    </aside>
  );
}
